/** SEO-метаданные публичных страниц при переходах внутри приложения. */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { apiHooks } from "@/api";
import { routerPaths } from "../routerPaths";

const ORIGIN = "https://gridplanner.ru";
const DEFAULT_DESCRIPTION =
	"Онлайн-планировщик ремонта: начертите план помещения, расставьте мебель и получите размеры, развёртки стен и 3D-вид.";

const pages: Record<string, { title: string; description: string }> = {
	[routerPaths.home]: {
		title: "Планировщик ремонта онлайн: чертёж, развёртки и 3D",
		description: DEFAULT_DESCRIPTION,
	},
	[routerPaths.floorPlanLanding]: {
		title: "Планировка квартиры онлайн",
		description:
			"Начертите план квартиры по размерам, расставьте мебель и посмотрите результат в 3D.",
	},
	[routerPaths.kitchenLanding]: {
		title: "Конструктор кухни онлайн",
		description:
			"Соберите кухню по размерам помещения: выберите форму, секции и фасады и посмотрите её в 3D.",
	},
	[routerPaths.roomLanding]: {
		title: "Планировка комнаты онлайн",
		description: "Создайте план комнаты по размерам, добавьте двери и окна и расставьте мебель.",
	},
	[routerPaths.interior3dLanding]: {
		title: "Планировка интерьера в 3D",
		description: "Создайте план помещения, расставьте мебель и посмотрите интерьер в 3D.",
	},
	[routerPaths.wardrobeLanding]: {
		title: "Конструктор шкафа онлайн",
		description: "Соберите шкаф по размерам, настройте секции и посмотрите результат в 3D.",
	},
	[routerPaths.elevationsLanding]: {
		title: "Развёртки стен онлайн",
		description: "Получите развёртки стен по чертежу с размерами, проёмами и объектами.",
	},
	[routerPaths.estimateLanding]: {
		title: "Смета ремонта по плану",
		description: "Рассчитайте предварительные объёмы и стоимость работ и материалов по плану.",
	},
	[routerPaths.blog]: {
		title: "Блог о планировании ремонта",
		description:
			"Практические инструкции по планировке помещений, электрике и подготовке к ремонту.",
	},
	[routerPaths.furnitureGenerator]: {
		title: "Конструктор мебели онлайн",
		description: "Соберите модуль, шкаф или кухню под нужный размер в конструкторе мебели.",
	},
	[routerPaths.editor2d]: {
		title: "Создать планировку онлайн",
		description: "Начертите план помещения, добавьте двери и окна, расставьте мебель.",
	},
	[routerPaths.editor3d]: {
		title: "Планировка помещения в 3D",
		description: "Посмотрите план помещения и расставленную мебель в 3D.",
	},
	[routerPaths.editorElevations]: {
		title: "Развёртки стен онлайн",
		description: "Получите развёртки стен помещения с размерами и электрикой по вашему плану.",
	},
	[routerPaths.editorEstimate]: {
		title: "Смета ремонта по плану",
		description: "Рассчитайте объёмы работ и материалов по плану помещения.",
	},
};

export const PageMetadata = () => {
	const { pathname, search } = useLocation();
	const articleId = pathname.startsWith(`${routerPaths.blog}/`) ? pathname.slice(-36) : "";
	const { data: article } = apiHooks.blog.useBlogArticle(articleId);

	useEffect(() => {
		const type = new URLSearchParams(search).get("type");
		const constructorType =
			pathname === routerPaths.furnitureGenerator && (type === "kitchen" || type === "wardrobe")
				? type
				: null;
		const page =
			constructorType === "kitchen"
				? {
						title: "Конструктор кухни онлайн",
						description: "Соберите кухню по размерам помещения в конструкторе мебели.",
					}
				: constructorType === "wardrobe"
					? {
							title: "Конструктор шкафа онлайн",
							description: "Соберите шкаф по своим размерам в конструкторе мебели.",
						}
					: article
						? { title: article.title, description: article.excerpt }
						: (pages[pathname] ?? { title: "GRID PLANNER", description: DEFAULT_DESCRIPTION });
		const title = `${page.title} | GRID PLANNER`;
		const canonicalPath = article ? routerPaths.blogArticleUrl(article.slug, article.id) : pathname;
		const canonical = `${ORIGIN}${canonicalPath}${constructorType ? `?type=${constructorType}` : ""}`;
		document.title = title;
		for (const [selector, value] of [
			['meta[name="description"]', page.description],
			['meta[property="og:title"]', title],
			['meta[property="og:description"]', page.description],
			['meta[property="og:url"]', canonical],
			['meta[name="twitter:title"]', title],
			['meta[name="twitter:description"]', page.description],
		]) {
			document.querySelector(selector)?.setAttribute("content", value);
		}
		let canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
		if (canonicalLink === null) {
			canonicalLink = document.createElement("link");
			canonicalLink.rel = "canonical";
			document.head.append(canonicalLink);
		}
		canonicalLink.href = canonical;
	}, [pathname, search, article]);

	return null;
};
